import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon, Business as BusinessIcon } from '@mui/icons-material';
import debounce from 'lodash/debounce';
import axios from 'axios';
import { glassTextField, getStaggeredAnimation } from './glassMorphismStyles';

export interface Sector {
  id: string;
  slug: string;
  name: string;
  summary80: string;
}

interface GlassSectorSelectProps {
  value?: string; // Can be sector name or ID
  onChange: (sectorName: string, sectorObj?: Sector) => void;
  error?: string;
  animationDelay?: number;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const GlassSectorSelect: React.FC<GlassSectorSelectProps> = ({
  value = '',
  onChange,
  error,
  animationDelay = 0,
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [options, setOptions] = useState<Sector[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedSector, setSelectedSector] = useState<Sector | null>(null);
  const prevValueRef = useRef(value);

  const fetchSectors = useCallback(
    debounce(async (query: string) => {
      if (!query || query.trim().length < 2) {
        setOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/v1/sectors/search`, {
          params: { query }
        });

        const sectors: Sector[] = response.data?.sectors ?? [];
        setOptions(sectors);
      } catch (fetchError) {
        console.error('Error fetching sectors:', fetchError);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    return () => {
      fetchSectors.cancel();
    };
  }, [fetchSectors]);

  useEffect(() => {
    if (open) {
      fetchSectors(inputValue);
    }
  }, [open, inputValue, fetchSectors]);

  useEffect(() => {
    if (prevValueRef.current === value) {
      return;
    }
    prevValueRef.current = value;

    if (!value) {
      setInputValue('');
      setSelectedSector(null);
      return;
    }

    setInputValue(value);

    const resolveSelected = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/v1/sectors/search`, {
          params: { query: value }
        });

        const sectors: Sector[] = response.data?.sectors ?? [];
        if (sectors.length === 0) {
          setSelectedSector(null);
          return;
        }

        const match = sectors.find(
          (sector) => sector.name.toLowerCase() === value.toLowerCase()
        ) || sectors[0];

        setSelectedSector(match);
        setOptions((prev) => {
          if (prev.some((sector) => sector.id === match.id)) {
            return prev;
          }
          return [match, ...prev];
        });
      } catch (err) {
        console.error('Error resolving sector for preset:', err);
      }
    };

    resolveSelected();
  }, [value]);

  const handleChange = (_event: any, newValue: Sector | string | null) => {
    if (typeof newValue === 'string') {
      setSelectedSector(null);
      setInputValue(newValue);
      onChange(newValue);
    } else if (newValue) {
      setSelectedSector(newValue);
      setInputValue(newValue.name);
      onChange(newValue.name, newValue);
    } else {
      setSelectedSector(null);
      setInputValue('');
      onChange('');
    }
  };

  const renderOption = (props: any, option: Sector) => (
    <Box
      component="li"
      {...props}
      sx={{
        color: 'rgba(255, 255, 255, 0.9) !important',
        padding: '12px 16px !important',
        borderBottom: '1px solid rgba(10, 124, 160, 0.1)',
        '&:last-child': {
          borderBottom: 'none',
        },
        '&:hover': {
          backgroundColor: 'rgba(10, 124, 160, 0.2)',
        },
        '&[aria-selected="true"]': {
          backgroundColor: 'rgba(10, 124, 160, 0.3)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <BusinessIcon
          sx={{
            mt: 0.5,
            color: 'rgba(10, 124, 160, 0.8)',
            fontSize: 20
          }}
        />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'inherit' }}>
            {option.name}
          </Typography>
          {option.summary80 && (
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                display: 'block',
                mt: 0.25
              }}
            >
              {option.summary80}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );

  const getOptionLabel = (option: Sector | string) => {
    if (typeof option === 'string') return option;
    return option?.name || '';
  };

  return (
    <Autocomplete
      size="small"
      freeSolo
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={selectedSector}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
        if (open) {
          fetchSectors(newInputValue);
        }
      }}
      options={options}
      loading={loading}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      filterOptions={(x) => x}
      isOptionEqualToValue={(option, val) => option.id === val.id || option.slug === val.slug}
      noOptionsText={
        inputValue.trim().length < 2
          ? 'Type at least 2 characters to search'
          : 'No sectors found'
      }
      loadingText="Searching sectors..."
      PaperComponent={(props) => (
        <Box
          {...props}
          sx={{
            backgroundColor: 'rgba(0, 51, 78, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(10, 124, 160, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            borderRadius: '8px',
            marginTop: '4px',
            '& .MuiAutocomplete-listbox': {
              padding: 0,
              maxHeight: '300px',
              '& .MuiAutocomplete-option': {
                padding: 0,
              },
            },
            '& .MuiAutocomplete-loading': {
              color: 'rgba(255, 255, 255, 0.7)',
              padding: '12px 16px',
            },
            '& .MuiAutocomplete-noOptions': {
              color: 'rgba(255, 255, 255, 0.7)',
              padding: '12px 16px',
            },
          }}
        />
      )}
      sx={{
        ...glassTextField,
        ...getStaggeredAnimation(3, animationDelay),
        '& .MuiInputBase-root': {
          color: 'rgba(255, 255, 255, 0.9)'
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Business Sector"
          placeholder="Search for your industry"
          error={Boolean(error)}
          helperText={error}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.6)' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={18} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default GlassSectorSelect;
